import React from "react";
import ReactSelect, { SingleValue } from "react-select";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options?: SelectOption[];
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  className?: string;
  style?: React.CSSProperties;
  name?: string;
  id?: string;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  isSearchable?: boolean;
  placeholder?: string;
  height?: number | string;
  menuPortal?: boolean;
}

export default function Select({
  options = [],
  value,
  onChange,
  className = "",
  style,
  name,
  id,
  disabled,
  onBlur,
  isSearchable = false,
  placeholder = "Select...",
  height = "33px",
  menuPortal = false,
}: SelectProps) {
  const selectedOption = options.find((o) => o.value === value) ?? null;

  const handleChange = (option: SingleValue<SelectOption>) => {
    if (!onChange || option === null) return;
    const syntheticEvent = {
      target: { value: option.value, name: name ?? "" },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
  };

  return (
    <ReactSelect<SelectOption>
      classNamePrefix="rs"
      className={className}
      styles={{
        control: (base, { isFocused }) => ({
          ...base,
          border: isFocused ? "1px solid var(--b)" : "1px solid var(--bd)",
          borderRadius: "8px",
          boxShadow: isFocused ? "none" : "none",
          minHeight: height,
          height: height,
          cursor: "pointer",
          ":hover": {
            border: isFocused ? "1px solid var(--b)" : "1px solid var(--bd)",
          },
          ...(style ?? {}),
        }),
        option: (base, { isSelected }) => ({
          ...base,
          fontFamily: "system-ui, sans-serif",
          backgroundColor: isSelected ? "var(--b)" : "transparent",
          color: isSelected ? "#fff" : base.color,
          cursor: "pointer",
          fontSize: "12px",
          ":hover": {
            backgroundColor: isSelected
              ? "var(--b)"
              : "color-mix(in srgb, var(--b) 15%, transparent)",
          },
          ":active": { backgroundColor: "var(--b)", color: "#fff" },
        }),
        valueContainer: (base) => ({ ...base, height, padding: "0 8px" }),
        indicatorsContainer: (base) => ({ ...base, height }),
        singleValue: (base) => ({ ...base, fontSize: "12px" }),
        placeholder: (base) => ({ ...base, fontSize: "12px" }),
        input: (base) => ({ ...base, fontSize: "12px", margin: 0, padding: 0 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      menuPortalTarget={menuPortal ? document.body : undefined}
      menuPosition={menuPortal ? "fixed" : "absolute"}
      options={options}
      value={selectedOption}
      onChange={handleChange}
      name={name}
      inputId={id}
      isDisabled={disabled}
      onBlur={onBlur}
      isSearchable={isSearchable}
      placeholder={placeholder}
      menuPlacement="auto"
    />
  );
}
